import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, TrendingUp, TrendingDown, Minus } from "lucide-react";

const VERDICT = {
  below_market: { label: "Great Value", color: "var(--emerald)", bg: "rgba(30,67,56,0.08)", icon: TrendingDown },
  fair: { label: "Fair Price", color: "var(--teak)", bg: "rgba(43,30,26,0.05)", icon: Minus },
  above_market: { label: "Above Market", color: "var(--primary)", bg: "rgba(200,90,50,0.08)", icon: TrendingUp },
  premium: { label: "Premium Ask", color: "var(--primary)", bg: "rgba(200,90,50,0.12)", icon: TrendingUp },
};

export const PriceInsight = ({ propertyId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!propertyId) return;
    api.get(`/properties/${propertyId}/insights`).then(({ data }) => setData(data)).catch(() => setData({ verdict: "sparse_data" }));
  }, [propertyId]);

  if (!data) return null;

  if (data.verdict === "sparse_data" || data.verdict === "unavailable") {
    return (
      <div className="p-4 rounded-2xl border border-[var(--line)] bg-white flex items-center gap-3" data-testid="price-insight-sparse">
        <Sparkles className="w-4 h-4 text-[var(--ink-muted)]" />
        <div className="text-xs text-[var(--ink-muted)]">{data.message || "More listings needed to show a price verdict for this locality."}</div>
      </div>
    );
  }

  const v = VERDICT[data.verdict] || VERDICT.fair;
  const Icon = v.icon;

  return (
    <div className="p-5 rounded-2xl border border-[var(--line)] bg-white" data-testid={`price-insight-${data.verdict}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[var(--primary)]" />
          <span className="text-xs uppercase tracking-wider font-semibold text-[var(--ink-muted)]">AI Price Insight</span>
        </div>
        <span className="text-[10px] text-[var(--ink-muted)]">{data.sample_size} comparable listings</span>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <div
          className="w-11 h-11 rounded-full flex items-center justify-center"
          style={{ background: v.bg, color: v.color }}
        >
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-serif text-xl" style={{ color: v.color }}>{v.label}</div>
          <div className="text-xs text-[var(--ink-muted)]">
            {data.diff_pct > 0 ? "+" : ""}{data.diff_pct}% vs locality median
          </div>
        </div>
      </div>
      <p className="text-sm text-[var(--ink-muted)] leading-relaxed">{data.message}</p>
      <div className="mt-3 pt-3 border-t border-[var(--line)] grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">This listing</div>
          <div className="font-mono text-sm font-bold text-[var(--teak)]">₹{data.listing_rate.toLocaleString("en-IN")}/sq.ft</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[var(--ink-muted)]">Locality median</div>
          <div className="font-mono text-sm font-bold text-[var(--teak)]">₹{data.locality_median_rate.toLocaleString("en-IN")}/sq.ft</div>
        </div>
      </div>
    </div>
  );
};
